// Client-side API calls and React hooks for content slots.
// Reads: public. Writes: require admin token from sessionStorage.

const API_BASE = import.meta.env.DEV ? 'http://localhost:3001' : '';

const TOKEN_KEY = 'gfs_admin_token';
function authHeaders(): Record<string, string> {
  const token = sessionStorage.getItem(TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export type ContentType = 'heading' | 'subheading' | 'paragraph' | 'button' | 'label' | 'caption';

export interface ContentSlot {
  slot_id: string;
  page_section: string;
  label: string;
  content: string;
  content_type: ContentType;
  updated_at: string;
}

// ── In-memory cache ───────────────────────────────────────────────────────────
let _cache: ContentSlot[] | null = null;
let _cacheExpiry = 0;
const CACHE_TTL = 60_000;

const _refreshListeners = new Set<() => void>();

export function invalidateContentCache() {
  _cache = null;
  _cacheExpiry = 0;
  _refreshListeners.forEach(cb => cb());
}

export async function fetchContentSlots(): Promise<ContentSlot[]> {
  if (_cache && Date.now() < _cacheExpiry) return _cache;
  try {
    const res = await fetch(`${API_BASE}/api/cms/content-slots`);
    if (!res.ok) throw new Error('fetch failed');
    const { slots } = await res.json() as { slots: ContentSlot[] };
    _cache = slots;
    _cacheExpiry = Date.now() + CACHE_TTL;
    return slots;
  } catch {
    return _cache ?? [];
  }
}

export async function saveContentSlot(
  slotId: string,
  content: string,
  contentType: ContentType,
  pageSection: string,
  label: string,
): Promise<ContentSlot> {
  const res = await fetch(`${API_BASE}/api/cms/content-slots/${encodeURIComponent(slotId)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ content, content_type: contentType, page_section: pageSection, label }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? 'Save failed');
  }
  const { slot } = await res.json() as { slot: ContentSlot };
  invalidateContentCache();
  return slot;
}

export async function deleteContentSlot(slotId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/cms/content-slots/${encodeURIComponent(slotId)}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? 'Delete failed');
  }
  invalidateContentCache();
}

// ── React hooks ───────────────────────────────────────────────────────────────
import { useState, useEffect, useCallback } from 'react';

export function useContentSlots() {
  const [slots, setSlots] = useState<ContentSlot[]>(_cache ?? []);
  const [loading, setLoading] = useState(!_cache);

  const doFetch = useCallback(() => {
    setLoading(true);
    fetchContentSlots()
      .then(s => { setSlots(s); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    doFetch();
    _refreshListeners.add(doFetch);
    return () => { _refreshListeners.delete(doFetch); };
  }, [doFetch]);

  const refetch = () => invalidateContentCache();
  return { slots, loading, refetch };
}

export function useContentSlot(slotId: string) {
  const { slots, loading } = useContentSlots();
  return { slot: slots.find(s => s.slot_id === slotId) ?? null, loading };
}

// ── Rendering helpers ─────────────────────────────────────────────────────────

// Converts stored text (with **bold**, *italic*, \n) to safe inline HTML.
// Only produces inline tags (strong, em, br) — never block elements.
// Input is HTML-escaped first so no injection is possible.
export function renderRichContent(text: string): string {
  let html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/gs, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/gs, '<em>$1</em>')
    .replace(/^- (.+)$/gm, '• $1')
    .replace(/\n/g, '<br/>');
  return html;
}
